import { Span, SpanStatusCode, trace, Tracer } from "@opentelemetry/api";
import { sanitizeLayout, sanitizeLayoutWrapper, stripAllLineTools, summarizeRemovals } from "./vendor/layout-sanitizer";
import { Chart, StudyTemplates } from "../models/charts";
import { analytics } from "../analytics/trackingPlan";
import * as tp from "@asthabhopal/tracking-plan";

var tracer: Tracer;

function initTracer() {
    tracer = trace.getTracer("vortex-tracer");
}

// Every layout request is bounded. The chart cannot become ready until the layout
// GET settles, so an unbounded fetch is a stuck chart with no error to show for it.
//
// 10s, deliberately generous: the boot watchdog is the real backstop for a slow
// boot, so this deadline only has to be short enough that a black-holed socket
// cannot hang forever. Too tight and a user on a bad network loses a layout that
// WOULD have arrived.
const LAYOUT_REQUEST_TIMEOUT_MS = 10000;

// A transport failure with a cause we can act on and label.
//   timeout      the deadline above fired
//   network      fetch itself rejected (offline, DNS, TLS)
//   http         a non-2xx status (carries .status)
//   unparseable  2xx whose body is not JSON (HTML error page from a gateway)
export type LayoutFailureReason = "timeout" | "network" | "http" | "unparseable";

export class LayoutRequestError extends Error {
    reason: LayoutFailureReason;
    status: number | null;
    constructor(reason: LayoutFailureReason, status?: number) {
        super(status ? `${reason} ${status}` : reason);
        this.name = "LayoutRequestError";
        this.reason = reason;
        this.status = status ?? null;
    }
}
export class SaveLoad {
    private _charts: Chart[] = [];
    private _studyTemplates: StudyTemplates[] = [];
    private _drawingTemplates: StudyTemplates[] = [];
    private _inHouseUrl: string;
    private _auth_token: string;
    // Lazily-started template fetches (memoized). Fetching eagerly in the
    // constructor cost two HTTP calls on every boot before the user ever
    // opened a template dialog — and doubled under dev/StrictMode double-mount.
    // Reset to null on failure so the next dialog open retries.
    private _getStudyTemplates: Promise<StudyTemplates[]> | null;
    private _getDrawingTemplates: Promise<StudyTemplates[]> | null;
    // The record (id + freshness stamp) of the layout most recently served by
    // getChartContent. Lets the container learn WHICH layout the library's own
    // load_last_chart boot applied — without a second chartsList/charts round
    // trip (it used to re-load the layout manually just to know this).
    private _lastLoadedRecord: { id: string; timestamp: number | null } | null = null;
    private _lastSavedRecord: { id: string; timestamp: number | null } | null = null;
    // Optional hook fired with the layout's primary symbol as soon as the
    // chart content arrives — BEFORE the library parses the layout and asks
    // the datafeed to resolve it. The container uses it to start the symbol
    // resolve early; the datafeed's in-flight dedup makes TV's own resolve a
    // piggyback instead of a second call. Best-effort only.
    private _onLayoutSymbol: ((symbol: string) => void) | null = null;
    // Optional hook fired after EVERY successful save, whatever triggered it
    // (autosave, repaint persist, manual Ctrl+S / menu save). The container
    // refreshes its layout signature here — one choke point instead of one
    // callback per save path; a missed path used to leave a stale signature
    // and cost a repaint+save on the next boot. Best-effort only.
    private _onSaved: (() => void) | null = null;
    // Last-resort symbol used to repair a saved layout whose symbol is empty/"-"
    // and that cannot be recovered from the layout's own data. flow-web boots via
    // load_last_chart with no per-open symbol, so the caller passes a sensible
    // default (NSE:NIFTYIDX) to guarantee a corrupt layout never hangs on the
    // loading spinner. Null when no usable fallback was provided.
    private _fallbackSymbol: string | null;
    // Layout ids we could not read cleanly this session, id -> reason. saveChart
    // refuses to POST an UPDATE to one of them: every transport failure ends with
    // the library showing a factory-default chart, and the autosave then writes
    // that blank state over the user's row — one reload on an expired token would
    // turn a transient 401 into permanent data loss.
    private _failedLayoutIds: Record<string, string> = {};
    // Set when the chartsList fetch itself failed, so we don't know what a save
    // would be overwriting.
    private _listFetchFailure: { reason: string; status: number | null } | null = null;
    // Saves run one at a time. The autosave and the theme-repaint persist can
    // otherwise be in flight together and, on a slow connection, the OLDER body can
    // arrive last and win.
    private _saveChain: Promise<unknown> = Promise.resolve();
    // Optional domain-log sink, supplied by the container (which owns chartLog).
    // Injected rather than imported so this module stays free of the OTLP exporter
    // and the node test loader can drive it without a browser.
    private _onLog: ((level: string, event: string, attrs: Record<string, any>) => void) | null = null;
    // Keys already logged this session (see _logOnce). A save-blocked layout and a
    // layout stuck with an empty symbol are re-evaluated on EVERY autosave tick, so
    // logging each attempt would put ~700 identical records an hour into Loki for
    // one affected user (flow-web autosaves every 5s). The reason never changes.
    private _loggedOnce: Record<string, true> = {};
    constructor(inHouseUrl: string, auth_token: string, fallbackSymbol?: string, onLayoutSymbol?: (symbol: string) => void, onSaved?: () => void, onLog?: (level: string, event: string, attrs: Record<string, any>) => void) {
        this.init();
        tracer = tracer;
        this._charts = [];
        this._studyTemplates = [];
        this._drawingTemplates = [];
        this._inHouseUrl = inHouseUrl + "aux/";
        this._auth_token = auth_token;
        this._fallbackSymbol = (typeof fallbackSymbol === "string" && fallbackSymbol.trim() !== "" && fallbackSymbol !== "-")
            ? fallbackSymbol
            : null;
        this._onLayoutSymbol = (typeof onLayoutSymbol === "function") ? onLayoutSymbol : null;
        this._onSaved = (typeof onSaved === "function") ? onSaved : null;
        this._onLog = (typeof onLog === "function") ? onLog : null;
        this._getStudyTemplates = null;
        this._getDrawingTemplates = null;
    }

    init() {
        initTracer()
    }

    private _ensureStudyTemplates(): Promise<StudyTemplates[]> {
        if (!this._getStudyTemplates) {
            this._getStudyTemplates = this._getAllStudyTemplates().then(undefined, (err: any) => {
                this._getStudyTemplates = null;
                throw err;
            });
        }
        return this._getStudyTemplates;
    }

    private _ensureDrawingTemplates(): Promise<StudyTemplates[]> {
        if (!this._getDrawingTemplates) {
            this._getDrawingTemplates = this._getAllDrawingTemplates().then(undefined, (err: any) => {
                this._getDrawingTemplates = null;
                throw err;
            });
        }
        return this._getDrawingTemplates;
    }

    // Record of the last layout served to the library (id + freshness stamp).
    // Consumed by the container's theme-signature logic instead of a manual
    // re-load of the layout the library already applied via load_last_chart.
    getLastLoadedRecord(): { id: string; timestamp: number | null } | null {
        return this._lastLoadedRecord;
    }

    // Record of the last layout SAVED through this adapter (id + stamp from the
    // save response). Lets the container refresh its layout signature after a
    // save without a chartsList round trip.
    getLastSavedRecord(): { id: string; timestamp: number | null } | null {
        return this._lastSavedRecord;
    }

    private _recordLoaded(id: number, response: Chart) {
        const listed = this._charts.find((c: Chart) => String(c.id) === String(id));
        const stamp = (response && typeof (response as any).timestamp === 'number')
            ? (response as any).timestamp
            : (listed && typeof (listed as any).timestamp === 'number' ? (listed as any).timestamp : null);
        this._lastLoadedRecord = { id: String(id), timestamp: stamp };
    }

    getAllCharts(): Promise<Chart[]> {
        return tracer.startActiveSpan("GetAllCharts", async (span) => {
            return this._getAllCharts().then((_) => {
                return Promise.resolve(this._charts);
            }).catch((error) => {
                span.setStatus({ code: SpanStatusCode.ERROR });
                return Promise.reject(error)
            }).finally(() => {
                span.end()
            })
        })

    }

    _getAllCharts() {
        return this._inhouseRequest('chartsList').then((response: Chart[]) => {
            // A 2xx whose body is an object, e.g. {status:"error"}: the old code ran
            // .filter on it and threw a bare TypeError from inside a callback.
            if (response !== null && response !== undefined && !Array.isArray(response)) {
                throw new LayoutRequestError("unparseable");
            }
            const raw = (response || [])
            // Drop layouts that are corrupt AND unrepairable so they can't be picked
            // from the "Load layout" list. List entries that arrive without content
            // can't be judged here, so keep them — repair still runs on getChartContent.
            this._charts = raw.filter((c: Chart) => {
                if (!c || typeof c.id === 'undefined') { return false }
                if (typeof c.content !== 'string' || c.content.trim() === '') { return true }
                return this._hasUsableContent(c) || this._canRepair(c)
            })
            return Promise.resolve()
        }).catch((error: any) => {
            // Record, then keep rejecting exactly as before — the container already
            // handles a failed list. The record is what blocks a destructive save.
            this._listFetchFailure = {
                reason: (error && error.reason) || 'network',
                status: (error && error.status) || null,
            };
            throw error;
        })
    }

    removeChart(id: number): Promise<void> {
        return tracer.startActiveSpan("RemoveChart", async (span) => {
            for (var i = 0; i < this._charts.length; ++i) {
                if (this._charts[i].id.toString() === id.toString()) {

                    return this._inhouseRequest('charts/' + id.toString(), undefined, undefined, "DELETE").then((_) => {
                        this._charts.splice(i, 1);
                        return Promise.resolve()
                    }).catch((err) => { return Promise.reject(err) })
                        .finally(() => {
                            span.end()
                        })
                }
            }
            span.end()
            return Promise.reject();
        })

    }

    // Saves are serialised. Two triggers (the autosave and the theme-repaint
    // persist) can fire close together, and with no in-flight guard the older body
    // can arrive last on a slow connection and win. A failed save must not poison
    // the chain, hence the swallowing catch on the stored tail — the caller still
    // receives the real rejection through `run`.
    saveChart(chartData: Chart): Promise<string> {
        const run = this._saveChain.catch(() => { }).then(() => this._doSaveChart(chartData));
        this._saveChain = run.catch(() => { });
        return run;
    }

    private _doSaveChart(chartData: Chart): Promise<string> {
        chartData.timestamp = new Date().valueOf();
        return tracer.startActiveSpan("SaveChart", async (span) => {
            // Never write to a layout we could not read. Narrow on purpose: only an
            // UPDATE to an id we failed to read is refused, so creating a layout and
            // "Save as" — neither of which can overwrite anything — keep working.
            //
            // RESOLVE here (unlike the invalid-symbol guard below): this row is not
            // writable for the rest of the session, so leaving the chart dirty would
            // only make the library re-attempt a save we will refuse again every 5s.
            const blockedReason = this._saveBlockReason(chartData);
            if (blockedReason) {
                span.addEvent("chart.save_blocked", { layoutId: String((chartData as any).id), reason: blockedReason });
                this._logOnce("save_blocked:" + String((chartData as any).id), "warn", "chart.save_blocked",
                    { layoutId: String((chartData as any).id), reason: blockedReason });
                span.end();
                return Promise.resolve("");
            }
            // Root-cause guard: never persist a layout whose symbol is empty/"-" at
            // the wrapper or on any MainSeries. TV autosave can fire mid symbol-change/
            // resolve with a transient empty symbol — that is how the corrupt rows were
            // generated.
            //
            // REJECT, do not resolve. Verified against v32's ChartSaverBase: it records
            // _prevChartState only in the SUCCESS callback, and _saveChart skips the
            // next save only when the new state deep-equals that record. So a rejection
            // leaves the chart dirty and the very next autosave tries again — which is
            // what we want, because an empty symbol is transient. Resolving instead
            // marked the layout clean and silently dropped the user's edit until they
            // happened to change something else.
            if (!this._isValidForSave(chartData)) {
                span.addEvent("chart.save_skipped_invalid_symbol", { symbol: chartData.symbol ?? "" });
                this._logOnce("save_skipped:" + String((chartData as any).id ?? ""), "warn", "chart.save_skipped",
                    { layoutId: String((chartData as any).id ?? ""), reason: "invalid_symbol" });
                span.end();
                return Promise.reject(new Error("save_skipped_invalid_symbol"));
            }
            // Strip drawings that would hang a later restore BEFORE they reach the
            // database. This sanitises the PAYLOAD, not the trigger, so the 5s
            // autosave, the theme-repaint persist and a manual save are all covered
            // by the same code. The live chart is untouched: an in-progress drawing
            // is missing from this one POST and lands in the next save once finished.
            const sanitized = sanitizeLayoutWrapper(chartData.content);
            if (sanitized.removed.length > 0) {
                chartData.content = sanitized.content;
                span.addEvent("chart.layout_sanitized", {
                    phase: "save",
                    removed: sanitized.removed.length,
                    byRule: JSON.stringify(summarizeRemovals(sanitized.removed)),
                });
                // Once per session: the live chart still holds the broken drawing, so
                // EVERY subsequent autosave strips it again. One record says it all.
                this._logOnce("sanitized:save", "warn", "chart.layout_sanitized", {
                    phase: "save",
                    removed: sanitized.removed.length,
                    byRule: JSON.stringify(summarizeRemovals(sanitized.removed)),
                });
            }
            return this._inhouseRequest('charts', chartData, undefined, "POST").then((response: Chart) => {
                var index: number = this._charts.findIndex((t: Chart) => t.id == chartData.id)
                if (index == -1) {
                    // A new chart layout was saved. No layout-specific element exists
                    // in the plan yet (candidate: ELEMENT_CHART_LAYOUT) — the chart
                    // element + label carries it meanwhile.
                    analytics.elementInteraction({ flow: tp.Flow.CHARTS, screen: tp.Screen.CHART, action: tp.Action.TAPPED, element: tp.Element.CHART, label: "layout_created" })
                    this._charts.push(response);
                } else {
                    this._charts[index] = response
                }
                // Remember the saved row's identity + freshness stamp so the
                // container's post-save signature refresh can read it directly
                // instead of re-fetching the whole chartsList after every save.
                // ⚠️ Use the CLIENT-SET stamp (chartData.timestamp) — verified
                // 2026-07-07: the save RESPONSE echoes the PREVIOUS row (its
                // timestamp is the prior save's), while the server persists the
                // client-sent value; writing the response stamp into the layout
                // signature caused a repaint+save loop on every boot.
                this._lastSavedRecord = {
                    id: String(response.id),
                    timestamp: (typeof chartData.timestamp === 'number') ? chartData.timestamp : null,
                };
                if (this._onSaved) {
                    try { this._onSaved(); } catch (e) { /* best-effort */ }
                }
                return Promise.resolve(response.id.toString());
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })

    }

    // ----- v32 layout-compat shim -------------------------------------------
    // v26-era layouts carry MainSeries style-study objects with `inputs` but no
    // `studyId` (seen in prod blobs: volFootprintStyle). v32's
    // _restoreMainSeriesStudyInputs derives ids for style studies and crashes on
    // the missing/unavailable id (parseIdString(null) -> "Cannot read properties
    // of null (reading 'indexOf')"), degrading first paint. Normalize: drop such
    // style objects — the library regenerates clean defaults; users lose only
    // sub-settings of a chart style they weren't using.
    // A/B kill switch for verification: window.__tvLayoutShimOff = true.
    // Mutates `inner` in place; returns true when something was dropped.
    private _dropBrokenMainSeriesStyles(inner: any): boolean {
        if (typeof window !== 'undefined' && (window as any).__tvLayoutShimOff) { return false; }
        let touched = false;
        for (const chart of inner.charts) {
            if (!chart || !Array.isArray(chart.panes)) { continue; }
            for (const pane of chart.panes) {
                if (!pane || !Array.isArray(pane.sources)) { continue; }
                for (const src of pane.sources) {
                    if (!src || src.type !== 'MainSeries' || !src.state) { continue; }
                    for (const key of Object.keys(src.state)) {
                        if (!/Style$/.test(key)) { continue; }
                        const st = (src.state as any)[key];
                        if (st && typeof st === 'object' && st.inputs && typeof st.studyId !== 'string') {
                            delete (src.state as any)[key];
                            touched = true;
                        }
                    }
                }
            }
        }
        return touched;
    }

    // Prepare stored layout content for the library. Two INDEPENDENT transforms,
    // each with its own kill switch, sharing ONE parse of the wrapper — this runs
    // on first paint, so a second JSON.parse of a ~24KB blob is worth avoiding:
    //
    //   1. _dropBrokenMainSeriesStyles  the v26->v32 compat shim
    //   2. sanitizeLayout               removes drawings that hang the restore
    //
    // Fail-open: any parse error returns the original content untouched, and the
    // string is only re-serialised when a transform actually changed something.
    private _prepareContentForLoad(content: string, id: number, span?: Span): string {
        if (typeof content !== 'string') { return content; }
        try {
            const wrapper = JSON.parse(content);
            if (!wrapper || typeof wrapper.content !== 'string') { return content; }
            const inner = JSON.parse(wrapper.content);
            if (!inner || !Array.isArray(inner.charts)) { return content; }
            const shimTouched = this._dropBrokenMainSeriesStyles(inner);
            // Drawings-safe mode: the boot watchdog observed the restore crash on a
            // previous attempt, so serve this ONE load with no drawings at all. The id
            // is marked save-blocked, so the stripped layout can never be persisted —
            // the drawings stay in the database.
            if (this._isDrawingsSafeMode(id)) {
                const stripped = stripAllLineTools(inner);
                // Block writes to this id directly rather than via
                // _markLayoutUnreadable: that helper also records chart.load_failed,
                // and a safe-mode boot SUCCEEDS (degraded). Counting it as a failure
                // would inflate the stuck-chart rate with boots that actually worked.
                this._failedLayoutIds[String(id)] = "safe_mode";
                span?.addEvent("chart.layout_safe_mode", { layoutId: String(id), stripped: stripped.length });
                this._logOnce("safe_mode:" + String(id), "warn", "chart.layout_safe_mode",
                    { layoutId: String(id), stripped: stripped.length });
                wrapper.content = JSON.stringify(inner);
                return JSON.stringify(wrapper);
            }
            const removed = sanitizeLayout(inner);
            if (removed.length > 0) {
                span?.addEvent("chart.layout_sanitized", {
                    phase: "load",
                    removed: removed.length,
                    byRule: JSON.stringify(summarizeRemovals(removed)),
                });
                this._logOnce("sanitized:load:" + String(id), "warn", "chart.layout_sanitized", {
                    phase: "load",
                    layoutId: String(id),
                    removed: removed.length,
                    byRule: JSON.stringify(summarizeRemovals(removed)),
                });
            }
            if (!shimTouched && removed.length === 0) { return content; }
            wrapper.content = JSON.stringify(inner);
            return JSON.stringify(wrapper);
        } catch (e) {
            return content;
        }
    }

    getChartContent(id: number): Promise<string> {
        return tracer.startActiveSpan("GetChartContent", async (span) => {
            return this._inhouseRequest('charts/' + id.toString(), undefined, undefined, "GET").then((response: Chart) => {
                // Happy path: the wrapper already has a usable symbol.
                if (this._hasUsableContent(response)) {
                    this._recordLoaded(id, response);
                    // Kick the symbol resolve now (exchange-qualified symbols
                    // only — bare legacy rows skip the prime, no harm done).
                    const layoutSymbol = (response as any).symbol;
                    if (this._onLayoutSymbol && typeof layoutSymbol === 'string' && layoutSymbol.indexOf(':') !== -1) {
                        try { this._onLayoutSymbol(layoutSymbol); } catch (e) { /* best-effort */ }
                    }
                    return Promise.resolve(this._serveContent(response.content, id, span));
                }
                // Corrupt symbol: repair from the layout's own data, then the fallback.
                const repaired = this._tryRepair(response);
                if (repaired !== null) {
                    this._recordLoaded(id, response);
                    span.addEvent("chart.repaired_on_load", { chartId: id });
                    this._logOnce("repaired:" + String(id), "warn", "chart.layout_repaired", { layoutId: String(id) });
                    // Signal the container that THIS load was a repaired layout. Its saved
                    // price range is untrustworthy (auto-scale may be off with a stale/locked
                    // range from the corrupt state), so the chart can re-fit the Y axis once
                    // on first data load instead of the user having to click "Auto".
                    if (typeof window !== "undefined") { (window as any).__chartLayoutRepaired = true; }
                    return Promise.resolve(this._serveContent(repaired, id, span));
                }
                // Unrepairable: reject cleanly so TV fails fast to a default chart
                // instead of hanging forever on the loading spinner.
                span.setStatus({ code: SpanStatusCode.ERROR, message: "invalid_chart_content" });
                return Promise.reject("invalid_chart_content");
            }).catch((error) => {
                // Whatever went wrong, this layout is now off-limits for writing: the
                // library is about to show a factory-default chart and the autosave
                // would otherwise persist that over the user's row.
                this._markLayoutUnreadable(id, this._loadFailureReason(error), span);
                console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error)
            })
                .finally(() => {
                    span.end()
                })
        })
    }

    // The single exit point for layout content handed to the library. Runs the
    // load-time transforms, then opens the boot watchdog's RESTORE WINDOW: from here
    // until the chart is ready, an error is attributable to restoring THIS layout,
    // which is the evidence the watchdog's one recovery attempt requires.
    private _serveContent(contentStr: string, id: number, span?: Span): string {
        const prepared = this._prepareContentForLoad(contentStr, id, span);
        const wd = (typeof window !== "undefined") ? (window as any).__chartBootWatchdog : null;
        if (wd) { try { wd.layoutServed(id); } catch (_) { /* best-effort */ } }
        return prepared;
    }

    // True when the host asked for this layout to be served without drawings — the
    // boot watchdog's recovery path. `true` means "whatever layout loads", an id
    // means that one only.
    private _isDrawingsSafeMode(id: number): boolean {
        if (typeof window === "undefined") { return false; }
        const flag = (window as any).__chartDrawingsSafeMode;
        if (flag === undefined || flag === null || flag === false) { return false; }
        if (flag === true) { return true; }
        return String(flag) === String(id);
    }

    // Map a load failure onto the closed reason vocabulary the dashboards group by.
    private _loadFailureReason(error: any): string {
        if (error instanceof LayoutRequestError) {
            return error.reason === "timeout" ? "layout_fetch_timeout" : "layout_fetch_error";
        }
        return "layout_invalid";
    }

    // Emit a record at most once per session per key, to Loki via the container's
    // sink. For conditions re-evaluated on a timer, one record carries the whole
    // story and hundreds do not.
    private _logOnce(key: string, level: string, event: string, attrs: Record<string, any>): void {
        if (this._loggedOnce[key]) { return; }
        this._loggedOnce[key] = true;
        if (this._onLog) { try { this._onLog(level, event, attrs); } catch (_) { /* best-effort */ } }
    }

    // Remember that this layout could not be read, so no later save can clobber it.
    // The span event stays (it is useful in Tempo) and is MIRRORED to Loki, so the
    // stuck-chart dashboard covers this surface exactly as it covers mobile_charts.
    private _markLayoutUnreadable(id: number, reason: string, span?: Span): void {
        this._failedLayoutIds[String(id)] = reason;
        span?.addEvent("chart.load_failed", { layoutId: String(id), reason });
        this._logOnce("load_failed:" + String(id), "error", "chart.load_failed", { layoutId: String(id), reason });
    }

    // Non-null when a save must be refused, carrying the reason for telemetry.
    // Only an UPDATE can be blocked — a create has no id and cannot overwrite.
    private _saveBlockReason(chartData: Chart): string | null {
        if (!chartData) { return null; }
        const id = (chartData as any).id;
        if (id === undefined || id === null || id === "") { return null; }
        const failed = this._failedLayoutIds[String(id)];
        if (failed) { return failed; }
        // The list fetch failed, so we never learned what is actually stored under
        // this id and cannot know what an update would replace.
        if (this._listFetchFailure) { return "layout_list_" + this._listFetchFailure.reason; }
        return null;
    }

    // ----- corrupt-chart-JSON repair helpers ---------------------------------
    // A saved layout's `content` is the TradingView "wrapper" (symbol envelope plus
    // a double-encoded inner layout string). Corruption seen in prod = the symbol
    // goes empty/"-", which leaves TV stuck on the loading spinner. These helpers
    // detect that, recover a symbol from up to four places, and repair or drop it.

    private _isNonEmptySymbol(s: unknown): s is string {
        return typeof s === "string" && s.trim() !== "" && s !== "-";
    }

    // chart.content -> wrapper object (outer envelope).
    private _parseWrapper(chart: Chart | null | undefined): any {
        if (!chart || typeof chart.content !== "string" || chart.content.trim() === "") { return null; }
        try { return JSON.parse(chart.content); } catch { return null; }
    }

    // wrapper.content (double-encoded) -> inner TradingView layout root.
    private _parseInnerLayout(wrapper: any): any {
        if (!wrapper || typeof wrapper.content !== "string" || wrapper.content.trim() === "") { return null; }
        try { return JSON.parse(wrapper.content); } catch { return null; }
    }

    // Walk every MainSeries state across ALL charts and panes (not just the first),
    // so multi-chart / multi-pane layouts are covered too.
    private _forEachMainSeriesState(layout: any, fn: (state: any) => void): void {
        const charts = layout && layout.charts;
        if (!Array.isArray(charts)) { return; }
        for (const c of charts) {
            const panes = c && c.panes;
            if (!Array.isArray(panes)) { continue; }
            for (const pane of panes) {
                const sources = pane && pane.sources;
                if (!Array.isArray(sources)) { continue; }
                for (const src of sources) {
                    if (src && src.type === "MainSeries" && src.state) { fn(src.state); }
                }
            }
        }
    }

    private _hasUsableContent(chart: Chart): boolean {
        const wrapper = this._parseWrapper(chart);
        return wrapper !== null && this._isNonEmptySymbol(wrapper.symbol);
    }

    private _canRepair(chart: Chart): boolean {
        const wrapper = this._parseWrapper(chart);
        return wrapper !== null && this._findRecoverySymbol(wrapper) !== null;
    }

    // Recovery order: wrapper.short_name -> wrapper.charts_symbols -> inner MainSeries
    // (symbol, then shortName; any chart/pane) -> constructor fallback (NSE:NIFTYIDX).
    private _findRecoverySymbol(wrapper: any): string | null {
        if (!wrapper) { return null; }
        if (this._isNonEmptySymbol(wrapper.short_name)) { return wrapper.short_name; }

        const fromMap = this._symbolFromChartsSymbols(wrapper.charts_symbols);
        if (this._isNonEmptySymbol(fromMap)) { return fromMap; }

        const layout = this._parseInnerLayout(wrapper);
        let recovered: string | null = null;
        if (layout) {
            this._forEachMainSeriesState(layout, (state) => {
                if (recovered) { return; }
                if (this._isNonEmptySymbol(state.symbol)) { recovered = state.symbol; }
                else if (this._isNonEmptySymbol(state.shortName)) { recovered = state.shortName; }
            });
        }
        if (recovered) { return recovered; }

        return this._fallbackSymbol;
    }

    // wrapper.charts_symbols is a stringified map, e.g. {"1":{"symbol":"NSE:IDEA"}}.
    private _symbolFromChartsSymbols(raw: unknown): string | null {
        if (typeof raw !== "string" || raw.trim() === "") { return null; }
        try {
            const map = JSON.parse(raw);
            if (map && typeof map === "object") {
                for (const key of Object.keys(map)) {
                    const entry = (map as any)[key];
                    if (entry && this._isNonEmptySymbol(entry.symbol)) { return entry.symbol; }
                }
            }
        } catch { /* ignore */ }
        return null;
    }

    // Returns a repaired wrapper string, or null if no symbol can be recovered.
    private _tryRepair(chart: Chart): string | null {
        const wrapper = this._parseWrapper(chart);
        if (!wrapper) { return null; }
        const recovered = this._findRecoverySymbol(wrapper);
        if (!recovered) { return null; }

        if (!this._isNonEmptySymbol(wrapper.symbol)) { wrapper.symbol = recovered; }

        const layout = this._parseInnerLayout(wrapper);
        if (layout) {
            let mutated = false;
            this._forEachMainSeriesState(layout, (state) => {
                if (!this._isNonEmptySymbol(state.symbol)) { state.symbol = recovered; mutated = true; }
            });
            if (mutated) {
                try { wrapper.content = JSON.stringify(layout); } catch { /* leave inner untouched */ }
            }
        }
        try { return JSON.stringify(wrapper); } catch { return null; }
    }

    // A layout is safe to persist only if the wrapper symbol AND every MainSeries
    // symbol are non-empty. NOTE: the wrapper's `content` is double-encoded, so this
    // parses one level deeper than the mobile_charts original (which checked the
    // wrapper level and so never actually validated the inner series).
    private _isValidForSave(chartData: Chart): boolean {
        if (!chartData) { return false; }
        if (!this._isNonEmptySymbol(chartData.symbol)) { return false; }
        const wrapper = this._parseWrapper(chartData);
        if (wrapper) {
            if (wrapper.symbol !== undefined && !this._isNonEmptySymbol(wrapper.symbol)) { return false; }
            const layout = this._parseInnerLayout(wrapper);
            if (layout) {
                let invalid = false;
                this._forEachMainSeriesState(layout, (state) => {
                    if (state.symbol !== undefined && !this._isNonEmptySymbol(state.symbol)) { invalid = true; }
                });
                if (invalid) { return false; }
            }
        }
        return true;
    }
    // ----- end corrupt-chart-JSON repair helpers -----------------------------

    removeStudyTemplate(studyTemplateInfo: StudyTemplates): Promise<void> {
        console.error("remove study template", studyTemplateInfo)
        var id = studyTemplateInfo.name?.split("|")[0]
        for (var i = 0; i < this._studyTemplates.length; ++i) {
            if (this._studyTemplates[i].id?.toString() === id?.toString()) {
                return tracer.startActiveSpan("RemoveStudyTemplate", async (span) => {
                    return this._inhouseRequest('studyTemplates/' + id?.toString(), undefined, undefined, "DELETE").then((_) => {
                        this._studyTemplates.splice(i, 1);
                        return Promise.resolve()
                    }).catch((err) => { return Promise.reject(err) })
                        .finally(() => {
                            span.end()
                        })
                })
            }
        }

        return Promise.reject();
    }

    getStudyTemplateContent(studyTemplateData: StudyTemplates): Promise<string> {
        for (var i = 0; i < this._studyTemplates.length; ++i) {
            if (this._studyTemplates[i].name === studyTemplateData.name) {
                if (this._studyTemplates[i] !== undefined) {
                    return Promise.resolve(this._studyTemplates[i].content);
                } else {
                    return Promise.reject();
                }

            }
        }

        return Promise.reject();
    }

    saveStudyTemplate(studyTemplateData: StudyTemplates): Promise<void> {
        console.error("study template", studyTemplateData)

        studyTemplateData.timestamp = new Date().valueOf();
        var aa = JSON.stringify(studyTemplateData.meta_info)
        studyTemplateData.meta_info = aa
        return tracer.startActiveSpan("async ", async (span) => {
            return this._inhouseRequest('studyTemplates', studyTemplateData, undefined, "POST").then((response: StudyTemplates) => {
                response.name = response.id?.toString() + "|" + response.name
                var index: number = this._studyTemplates.findIndex((t: StudyTemplates) => t.id == studyTemplateData.id)
                if (index == -1) {
                    this._studyTemplates.push(response);
                } else {
                    this._studyTemplates[index] = response
                }
                return Promise.resolve();
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })
    }

    getAllStudyTemplates(): Promise<StudyTemplates[]> {
        return this._ensureStudyTemplates().then((_) => {
            return Promise.resolve(this._studyTemplates);
        })

    }

    _getAllStudyTemplates() {
        return tracer.startActiveSpan("GetAllStudyTemplates", async (span) => {
            return this._inhouseRequest('studyTemplates').then((response: StudyTemplates[]) => {
                this._studyTemplates = (response.map((aa: StudyTemplates) => {
                    aa.name = aa.id?.toString() + "|" + aa.name
                    aa.meta_info = JSON.parse(aa.meta_info as string)
                    return aa
                }) || [])
                return Promise.resolve(this._studyTemplates)
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })

    }

    _getAllDrawingTemplates() {
        return tracer.startActiveSpan("GetAllDrawingTemplates", async (span) => {
            return this._inhouseRequest('drawingTemplates').then((response: StudyTemplates[]) => {
                this._drawingTemplates = (response.map((aa: StudyTemplates) => {
                    aa.name = aa.name
                    aa.content = aa.content
                    return aa
                }) || [])
                return Promise.resolve(this._drawingTemplates)
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })
    }
    removeDrawingTemplate(templateName: string): Promise<void> {
        for (var i = 0; i < this._drawingTemplates.length; ++i) {
            if (this._drawingTemplates[i].name === templateName) {
                return tracer.startActiveSpan("RemoveDrawingTemplate", async (span) => {
                    return this._inhouseRequest(`drawingTemplates/${this._drawingTemplates[i].id}`, undefined, undefined, "DELETE").then((_) => {
                        this._drawingTemplates.splice(i, 1);
                        return Promise.resolve();
                    }).catch((err) => { return Promise.reject(err) })
                        .finally(() => {
                            span.end()
                        })
                })
            }
        }

        return Promise.reject();
    }

    loadDrawingTemplate(templateName: string): Promise<string> {
        for (var i = 0; i < this._drawingTemplates.length; ++i) {
            if (this._drawingTemplates[i].name === templateName) {
                return Promise.resolve(this._drawingTemplates[i].content);
            }
        }

        return Promise.reject();
    }

    saveDrawingTemplate(templateName: string, content: string): Promise<void> {
        // studyTemplateData.meta_info = JSON.stringify(studyTemplateData.meta_info)
        var payload: StudyTemplates = {
            name: templateName,
            content: content,
            id: 0
        }
        for (var i = 0; i < this._drawingTemplates.length; ++i) {
            if (this._drawingTemplates[i].name === templateName) {
                payload.id = this._drawingTemplates[i].id
                break;
            }
        }
        return tracer.startActiveSpan("SaveDrawingTemplate", async (span) => {
            return this._inhouseRequest('drawingTemplates', payload, undefined, "POST").then((response) => {
                response.name = payload.name
                response.id = response.drawing_template_id
                for (var i = 0; i < this._drawingTemplates.length; ++i) {
                    if (this._drawingTemplates[i].name === templateName) {
                        this._drawingTemplates.splice(i, 1);
                        break;
                    }
                }
                this._drawingTemplates.push(response);
                return Promise.resolve();
            }).catch((error) => { console.log(error); span.setStatus({ code: SpanStatusCode.ERROR }); return Promise.reject(error) })
                .finally(() => {
                    span.end()
                })
        })

    }

    getDrawingTemplates(): Promise<string[]> {
        return this._ensureDrawingTemplates().then((_) => {
            return Promise.resolve(this._drawingTemplates.map(function (template: StudyTemplates) {
                return template.name;
            }));
        })
    }
    // Bounded, and every failure mode is typed. Previously an unbounded fetch left
    // getAllCharts/getChartContent pending forever on a black-holed request, and a
    // gateway's HTML error page threw a bare SyntaxError from JSON.parse.
    private async _inhouseRequest(urlPath: string, body?: StudyTemplates, params?: Record<string, string>, method: string = "GET") {
        const headers = new Headers({
            "Authorization": `Bearer ${this._auth_token}`,
        });

        const options: RequestInit = {
            headers,
            method,
        };

        if (body !== undefined && method !== "GET") {
            options.body = JSON.stringify(body);
        }

        const url = new URL(`${this._inHouseUrl}${urlPath}`);
        if (params) {
            url.search = new URLSearchParams(params).toString();
        }

        const controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
        let timer: ReturnType<typeof setTimeout> | null = null;
        if (controller) {
            options.signal = controller.signal;
            timer = setTimeout(() => controller.abort(), LAYOUT_REQUEST_TIMEOUT_MS);
        }
        let response: Response;
        let responseText: string;
        try {
            response = await fetch(url.toString(), options);
            // Inside the deadline too: a server that sends headers and then stalls
            // the body would otherwise hang exactly as before.
            responseText = await response.text();
        } catch (error) {
            throw new LayoutRequestError(
                (controller && controller.signal.aborted) ? "timeout" : "network");
        } finally {
            if (timer !== null) { clearTimeout(timer); }
        }
        if (!response.ok) { throw new LayoutRequestError("http", response.status); }
        // A 204/empty body is a success for DELETE, which is why this resolves null
        // rather than failing on JSON.parse("").
        if (responseText.trim() === "") { return null; }
        try {
            return JSON.parse(responseText);
        } catch (_) {
            throw new LayoutRequestError("unparseable", response.status);
        }
    }

}